import { Injectable } from '@angular/core';
import { AddResourcesModalService } from '@app/flex-framework/components/modals/add-resources-modal/services/add-resources-modal.service';
import { FlexRow, FlexRowSummary } from '@app/flex-framework/flex-api.model';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { ModalService } from '@app/shared/services/modal.service';
import { SkillStandardsModalService } from '@app/skill-standards/services/skill-standards-modal.service';
import { TargetService } from '@app/target/services/target.service';
import { Resource, Target } from '@app/target/target-api.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TargetResourcesModal } from '../components/modals/target-resources-modal/target-resources-modal.component';
import { FlexService } from './flex-framework.service';

@Injectable({
  providedIn: 'root',
})
export class FlexRowResourceService {
  public i18n = this.translate.instant(['dgTagRating_RemoveFromPlan']);
  private orgManagedNotificationFlag = this.ldFlagsService.orgManagedLDFlag;

  constructor(
    private addResourcesModalService: AddResourcesModalService,
    private flexRowService: FlexRowService,
    private targetService: TargetService,
    private translate: TranslateService,
    private flexService: FlexService,
    private modalService: ModalService,
    private skillStandardsModalService: SkillStandardsModalService,
    private ldFlagsService: LDFlagsService
  ) {}

  public addResource(row: FlexRow, customAdd?): Observable<any> {
    // These last two cases need to be handled differently depending on whether we're using
    // new modals (Plans) or not, as `showAddResourcesForm` *used to be* the default path for
    // adding content, but going forward we want addResourcesModalService's `openModal` to be
    // the new default.
    // TODO: Delete this and the following showOldModals section once we are no longer using old
    // modals anywhere. For now, they're still in use in Opportunities and Skill Standards carousels.
    const showOldModals = !!customAdd;
    const target = this.targetService.targetResponse?.target;
    // Check for custom add methods usage first,
    // otherwise default to regular add resource modal.
    // This lets us also be backward compatible with rows of any type.
    if (row.flexRowType === 'SkillStandardsSkill') {
      return this.skillStandardsModalService
        .openAddTagsToSkillStandardsModal()
        .pipe(tap((tags) => customAdd('tags', tags)));
    } else if (row.flexRowType === 'SkillStandardsSegment') {
      return this.skillStandardsModalService
        .openAddCreateSegmentsToSkillStandardsModal(
          row.targetResources.map((resource) => ({
            ...resource,
            businessRuleId: resource.segmentId,
            ruleName: resource.name,
          }))
        )
        .pipe(tap((segments) => customAdd('segments', segments)));
    }
    // Show old, resources-specific modal, only used by opportunities right now:
    else if (showOldModals) {
      return this.showAddResourcesForm(
        row.section,
        row.targetResources,
        target,
        customAdd
      ).pipe(
        tap((resources: []) => {
          row = {
            ...this.flexRowService.updateRowResourceCount(
              row,
              resources.length
            ),
          };
        })
      );
    }
    // Our new default-case
    return this.addResourcesModalService
      .openModal({
        target: target,
        exclusionList: row.targetResources,
        sectionNode: row.section.parentNode,
        hideFeatures: !!customAdd,
      })
      .pipe(
        map((results) => {
          if (results?.selectedSearch?.length !== undefined) {
            row = {
              ...this.flexRowService.updateRowResourceCount(
                row,
                results.selectedSearch.length
              ),
            };
          }
        })
      );
  }

  public getModifyMenuConfigFn(row: FlexRow) {
    return (content, defaultConfig) => [
      {
        title: this.i18n.dgTagRating_RemoveFromPlan,
        isHidden: () => !this.flexService.isEditing.value,
        defaultAction: () => {
          const id = content.resourceId || content.providerId;
          this.flexRowService.removeResourceFromRow(row, id);
        },
      },
      ...defaultConfig,
    ];
  }

  public configureRowResourceMenu(
    row: FlexRow,
    resource: any,
    isEditing: boolean
  ): MenuViewModel[] {
    let menuConfig: MenuViewModel[] = [];

    // The LinkText and FreeFormText don't have resources
    if (row.flexRowType === 'LinkText' || row.flexRowType === 'FreeformText') {
      return menuConfig;
    }

    // Remaining resources can be removed from rows
    menuConfig = [
      {
        id: 'remove',
        title: this.i18n.dgTagRating_RemoveFromPlan,
        defaultAction: (_) => {
          // TODO: Find the id the correct way
          const id =
            resource.resourceId || resource.providerId || resource.resourceId;

          this.flexRowService.removeResourceFromRow(row, id);
        },
        isHidden: () => !isEditing,
      },
    ];

    return menuConfig;
  }

  public showAddResourcesForm(
    resourceSection: FlexRowSummary,
    existingResources = [],
    target?: Target,
    customSaveResources?
  ) {
    return this.modalService.show(TargetResourcesModal, {
      inputs: {
        resourceSection,
        existingResources,
        target,
        customSaveResources,
      },
    });
  }

  // Returns resources that are org-managed(have an organizationId)
  public checkForOrgManagedResources(
    resources: Resource[]
  ): [boolean, Resource[]] {
    if (!this.orgManagedNotificationFlag) {
      return [false, []];
    }
    const orgResourcesWithOrgId = resources.filter((item) => {
      return item?.reference.organizationId;
    });

    return [orgResourcesWithOrgId.length > 0, orgResourcesWithOrgId];
  }

  // Sets the organizationId on the target if resources contain an organizationId
  public setTargetOrgIdFromResource(resources) {
    if (
      this.orgManagedNotificationFlag &&
      !this.targetService.isTargetOrgManaged // Targets that are already org-managed do not need to add organizationId.
    ) {
      const [isOrgManagedResources, resourcesWithOrgId] =
        this.checkForOrgManagedResources(resources);
      let target = this.targetService?.targetResponse.target;

      target = {
        ...target,
        ...{
          organizationId: isOrgManagedResources
            ? resourcesWithOrgId[0].reference.organizationId
            : undefined,
        },
      };
      this.targetService.targetResponse.target = target;
    }
  }
}
